import React, { useState } from 'react';
import { BookFilled, FlagFilled } from '@ant-design/icons';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Button } from 'antd';
import { ArrowLeftIcon, DollarCircleIcon } from 'icons';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { openAuthModal } from 'modules/auth';
import { useStartPrivateTest } from 'modules/blok-test';
import {
  getUniversityCourseSelect,
  globalConst,
  ISecondRoot,
  useBack,
  useUniversityCourseBySlug
} from 'modules/global';
import { hasUniversityTest } from 'modules/private-universities';

import { ApplicationModal, NextHead } from 'components';

const Index = () => {
  const t = useTranslations();
  const { query } = useRouter();
  const dispatch = useAppDispatch();
  const { univerCourse } = useUniversityCourseBySlug({
    courseSlug: query?.directionName as string,
    universitySlug: query?.universityId as string
  });
  const { mutate, isLoading: startTestLoading } = useStartPrivateTest();
  const isRouteLoading = useAppSelector(state => state.routeLoading.isRouteLoading);
  const isAuthorized = useAppSelector(({ user }) => user.phone);
  const [openApplicationCreate, setOpenApplicationCreate] = useState(false);

  const onStartTest = () => {
    const rawData = {
      universitySlug: query?.universityId as string,
      courseSlug: query?.directionName as string
    };

    mutate(rawData);
  };

  const onBack = useBack(`${globalConst?.pageNames?.PRIVATE_UNIVERSITIES}/${query?.universityId}`);
  const testAvailable = hasUniversityTest(univerCourse?.university?.id);

  const onClick = () => {
    if (testAvailable) {
      return onStartTest();
    }

    if (!isAuthorized) {
      return dispatch(openAuthModal());
    }

    return setOpenApplicationCreate(true);
  };

  return (
    <>
      <NextHead title={univerCourse?.course_name} description={univerCourse?.description} />

      <div className="mx-auto my-7 min-h-screen max-w-[900px] px-3 lg:px-0">
        <div onClick={onBack} className="inline-flex cursor-pointer items-center justify-start gap-2 font-semibold">
          <ArrowLeftIcon /> {t('common.ortga_qaytish')}
        </div>
        <div className="mt-5 rounded-xl bg-white p-4">
          <h4 className="text-2xl font-bold">{univerCourse?.course_name?.toUpperCase()}</h4>
          <p className="text-sm text-gray-700">{univerCourse?.university?.name}</p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            <div className="flex items-center gap-3 rounded-xl bg-blue-100 px-3 py-2">
              <div className="flex size-8 items-center justify-center rounded-full bg-blue-500 text-white">
                <DollarCircleIcon />
              </div>
              <div>
                <span className="text-xs text-gray-600">{t('kontrakt_tolovi')}</span>
                <p className="text-sm font-semibold text-gray-900">
                  {univerCourse?.contract_amount ? (
                    <>{Number(univerCourse?.contract_amount).toLocaleString('ru')} - </>
                  ) : (
                    t('mavjud_emas')
                  )}{' '}
                  UZS
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-yellow-100 px-3 py-2">
              <div className="flex size-8 items-center justify-center rounded-full bg-yellow-500 text-white">
                <FlagFilled />
              </div>
              <div>
                <span className="text-xs text-gray-600">{t('TalimTili')}</span>
                <p className="text-sm font-semibold text-gray-900">{univerCourse?.language || t('mavjud_emas')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-indigo-100 px-3 py-2">
              <div className="flex size-8 items-center justify-center rounded-full bg-indigo-500 text-white">
                <BookFilled />{' '}
              </div>
              <div>
                <span className="text-xs text-gray-600">{t('TalimShakli')}</span>
                <p className="text-sm font-semibold text-gray-900">{univerCourse?.study || t('mavjud_emas')}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-5 rounded-xl bg-white p-4">
          <h4 className="mb-2 text-lg font-semibold">{t('yonalish_haqida')}</h4>
          <p className="text-sm">{univerCourse?.description || t('tavsif_mavjud_emas')}</p>

          {univerCourse?.duration && (
            <div className="mt-5 flex items-center gap-3">
              <span className="text-sm font-medium text-indigo-600">{t('oqish_davomiyligi')}:</span>
              <span className="text-sm">
                {univerCourse?.duration} {t('yil').toLowerCase()}
              </span>
            </div>
          )}

          {univerCourse?.direction?.passing_ball && (
            <div className="mt-2 flex items-center gap-3">
              <span className="text-sm font-medium text-indigo-600">{t('otish_bali')}:</span>
              <span className="text-sm">
                {univerCourse?.direction?.passing_ball} {t('ball').toLowerCase()}
              </span>
            </div>
          )}
          {univerCourse?.requirements && (
            <div className="mt-2 text-sm">
              <span className="font-medium text-indigo-600">{t('talablar')}: </span>
              <span className="text-xs">{univerCourse?.requirements}</span>
            </div>
          )}
        </div>
        <div className="mt-5 rounded-xl bg-white p-4">
          <h4 className="mb-2 text-lg font-semibold">{t('qoshimcha_malumotlar')}</h4>
          <div className="mt-3 grid grid-cols-2 items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 sm:grid-cols-3 md:grid-cols-4">
            <div>
              <p className="mb-1 text-xs text-gray-400">{t('test_davomiyligi')}</p>
              <h5 className="text-sm font-medium">
                {univerCourse?.direction?.duration} {t('daqiqa')}
              </h5>
            </div>
            <div>
              <p className="mb-1 text-xs text-gray-400">{t('savollar_soni')}</p>
              <h5 className="text-sm font-medium">
                {univerCourse?.direction?.total_question_count} {t('ta')}
              </h5>
            </div>
            <div>
              <p className="mb-1 text-xs text-gray-400">{t('Yonalishdagi_fanlar')}</p>
              <h5 className="text-sm font-medium">
                {univerCourse?.direction?.subjects?.map(item => item?.name).join(' / ')}
              </h5>
            </div>
            <div>
              <Button
                loading={startTestLoading || isRouteLoading}
                onClick={onClick}
                className="!w-full !rounded-lg !border-transparent !bg-green-500 !py-5 !font-medium !text-white"
              >
                {testAvailable ? t('imtihon_topshirish') : t('ArizaQoldirish')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ApplicationModal
        universityId={univerCourse?.university?.id}
        open={openApplicationCreate}
        onClose={() => setOpenApplicationCreate(false)}
      />
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const queryClient = new QueryClient();

  const courseSlug = params?.directionName as string;
  const universitySlug = params?.universityId as string;

  await queryClient.prefetchQuery({
    queryKey: [globalConst.queryKeys.GET_UNIVERSITY_COURSE_SELECT_BY_SLUG, { courseSlug, universitySlug }],
    queryFn: getUniversityCourseSelect({ courseSlug, universitySlug })
  });

  const universityCourseRes: { univerCourse: ISecondRoot } | undefined = queryClient.getQueryData([
    globalConst.queryKeys.GET_UNIVERSITY_COURSE_SELECT_BY_SLUG,
    { courseSlug, universitySlug }
  ]);

  if (
    `${universityCourseRes?.univerCourse?.university?.slug}/${universityCourseRes?.univerCourse?.slug}` !==
    `${universitySlug}/${courseSlug}`
  ) {
    return {
      notFound: true
    };
  }

  return {
    props: {
      dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
      messages: (await import(`../../../../messages/${locale}.json`)).default
    }
  };
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking'
  };
}
